import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { TableModule, TableLazyLoadEvent, Table } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { SupplierSiteService } from '../supplierSite.service';
import { SupplierSite } from '../supplierSite.model';
import { Page } from '../../../../models/page-wrapper.model';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import * as XLSX from 'xlsx';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputTextModule } from 'primeng/inputtext';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { IftaLabelModule } from 'primeng/iftalabel';

@Component({
  selector: 'app-supplierSite-list',
  templateUrl: './list-supplierSite.component.html',
  styleUrl: './list-supplierSite.component.css',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TableModule,
    ButtonModule,
    ToastModule,
    ConfirmDialogModule,
    RouterModule,
    InputGroupModule,
    InputGroupAddonModule,
    ReactiveFormsModule,
    InputTextModule,
    IftaLabelModule,
],
  providers: [ConfirmationService, MessageService],
})
export class ListSupplierSiteComponent implements OnInit {
  supplierSites: SupplierSite[] = [];
  totalRecords = 0;
  isLoading = false;
  @ViewChild('dt') dt!: Table;
  filterForm!: FormGroup;
  private filterSub!: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private SupplierSiteService: SupplierSiteService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.buildFilterForm();
    this.filterSub = this.filterForm.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(() => {
        this.dt.reset();
      });
  }
  buildFilterForm() {
    this.filterForm = this.fb.group({
      supplierSiteName: [''],
      supplierSiteNumber: [''],
    });
  }

  onSearch(): void {
    this.dt.reset();
  }

  onReset(): void {
    this.filterForm.reset({
      supplierSiteName: '',
      supplierSiteNumber: '',
    });
    this.dt.reset();
  }

  loadSupplierSite(
    event?: TableLazyLoadEvent,
    isExport = false,
    callback?: (supplierSites: SupplierSite[]) => void
  ) {
    this.isLoading = true;
    let params: any;
    if (isExport) {
      params = {
        page: 0,
        size: 2000,
        supplierSiteName: this.filterForm.value.supplierSiteName,
        supplierSiteNumber: this.filterForm.value.supplierSiteNumber,
      };
    } else {
      const size = event?.rows ?? 10;
      const page = event?.first ? Math.floor(event.first / size) : '0';
      params = {
        page: page,
        size: size,
        supplierSiteName: this.filterForm.value.supplierSiteName,
        supplierSiteNumber: this.filterForm.value.supplierSiteNumber,
      };
    }
     Object.keys(params).forEach(key => {
      if (!params[key]) {
        delete params[key];
      }
    });
    this.SupplierSiteService.getAllSupplierSites(params).subscribe({
      next: (page: Page<SupplierSite>) => {
        this.supplierSites = page.content || [];
        this.totalRecords = page.totalElements;
        this.isLoading = false;
         if (callback) {
          callback(page.content);
        }
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load Supplier Sites.',
        });
        this.isLoading = false;
      },
    });
  }

  deleteSupplierSite(supplierSite: SupplierSite): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete ${supplierSite.supplierSiteName}?`,
      header: 'Confirm Deletion',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.SupplierSiteService.deleteSupplierSite(
          supplierSite.supplierSiteId
        ).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Supplier Site deleted successfully',
            });
            this.loadSupplierSite();
          },
          error: () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to delete Supplier Site.',
            });
          },
        });
      },
    });
  }

  createSupplierSite() {
    this.router.navigate(
      ['.', { outlets: { canvasOutlet: ['supplier-site', 'create'] } }],
      { relativeTo: this.route.parent?.parent }
    );
  }
  editSupplierSite(id: number): void {
    this.router.navigate([
      '/ai-studio/session/current',
      { outlets: { canvasOutlet: ['supplier-site', 'edit', id] } },
    ]);
  }

  exportExcel(): void {
    this.loadSupplierSite(undefined, true, (supplierSites: SupplierSite[]) => {
      const dataToExport = supplierSites.map((supplierSite) => ({
        'Supplier Site Name': supplierSite.supplierSiteName,
        'Supplier Site Code': supplierSite.supplierSiteId,
        Business: supplierSite.business?.businessName,
        'Business Code': supplierSite.business?.businessCode,
        Supplier: supplierSite.supplier?.supplierName,
      }));

      const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataToExport);
      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Supplier Sites');
      XLSX.writeFile(wb, 'supplier_site_Export.xlsx');
    });
  }
}
